import type { RouteLocationNormalizedGeneric } from '#vue-router'

import {
  TUTOR_ATTENDANCE_RATES_ARRAY_QUERY_KEYS,
  getCanonicalTutorAttendanceRatesQuery,
  queryToTutorAttendanceRatesFilters,
  useTutorAttendanceRatesStore,
} from '@pages/tutor-attendance-rates'
import { useListsStore } from '@shared/model'
import { MAX_PER_PAGE, toPaginationValue } from '@shared/lib/routes'
import { foldBracketArrayParams } from '@shared/lib/url'

import { defineNuxtRouteMiddleware, navigateTo } from '#app'

/**
 * `/tutor-attendance-rates` — query → store filters, mirroring `middleware/surveys.ts`. A query the filters
 * cannot show as-is (`tutor[0]=…` arrays from the Tutor Kanban and app-main, a stale status or reason,
 * a lone `to`…) is rewritten once, so the URL, the controls and the request always agree and a
 * stale bookmark never gets a 422.
 */
export default defineNuxtRouteMiddleware(async (to: RouteLocationNormalizedGeneric) => {
  const foldedQuery = foldBracketArrayParams(to.query, TUTOR_ATTENDANCE_RATES_ARRAY_QUERY_KEYS)
  const query = foldedQuery ?? to.query

  // The `lists` middleware only starts its request; the known statuses and reasons are needed right now.
  const lists = await useListsStore().ensure()

  const filters = queryToTutorAttendanceRatesFilters(query, {
    statusLabels: lists?.booking?.status_finished_for_customer ?? null,
    reasonLabels: lists?.tutor_attendance_rate?.reason ?? null,
  })
  const canonicalQuery = getCanonicalTutorAttendanceRatesQuery(query, filters)

  if (foldedQuery || canonicalQuery) {
    return navigateTo({ path: to.path, query: canonicalQuery ?? query }, { replace: true })
  }

  const store = useTutorAttendanceRatesStore()

  store.setFilters(filters)
  store.setCurrentPage(toPaginationValue(to.query?.page, 1))
  store.setPerPage(toPaginationValue(to.query?.per_page, store.getPerPage, MAX_PER_PAGE))
})
